import React, { useState, useEffect, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { Lock, Plus, Receipt, TriangleAlert } from 'lucide-react'
import api from '../services/api'
import { VoidConfirmModal } from '../components/financial/MovementHistory'
import { money, todayISO, fmtDateTime } from '../components/financial/formatting'
import { monthLabel, rangeLabel, isForbidden, errorDetail } from '../components/corporate/overview'
import MonthRangePicker from '../components/corporate/MonthRangePicker'
import FocusMonthPicker from '../components/corporate/FocusMonthPicker'
import NotCountedPanel from '../components/corporate/NotCountedPanel'
import HeadlineKpis from '../components/corporate/HeadlineKpis'
import BankDebtPanel from '../components/corporate/BankDebtPanel'
import ExpenseByCategory from '../components/corporate/ExpenseByCategory'
import TaxLines from '../components/corporate/TaxLines'
import AllocationWaterfall from '../components/corporate/AllocationWaterfall'
import RecurringExpensesPanel from '../components/corporate/RecurringExpensesPanel'
import RegisterRecurringModal from '../components/corporate/RegisterRecurringModal'

// ─────────────────────────────────────────────────────────────────────────────
//   Company finances — gastos de la empresa
//
//   Los libros propios de la empresa, que no son la economía de los leads:
//   software, seguros, comisiones bancarias, contratistas. Hasta ahora vivían
//   en un Excel. El backend es `corporate/` (repo system, PR #105); su regla
//   fundacional es que un CashEntry NUNCA registra un cobro a un buyer ni un
//   pago a un afiliado — eso lo calcula `financial/` desde los propios leads, y
//   apuntarlo aquí además pondría el mismo dólar en los libros dos veces.
//
//   Esta pantalla es sólo el lado del gasto: `direction: 'out'`, fijo. No hay
//   selector de dirección porque una entrada de dinero (un reembolso de un
//   proveedor, una aportación) no es lo que esta pantalla registra, y ofrecer
//   el campo invitaría a usarla para eso.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//   Las rejillas de la pantalla, y las tres que NO existen
//
//   Casi todo aquí se lee de un vistazo y no necesita el ancho de la pantalla:
//   tres importes de deuda, ocho gastos fijos con la mitad derecha vacía, dos
//   bloques de impuestos de una cifra cada uno. En una sola columna eso son
//   varias pantallas de scroll para leer lo que cabe en una.
//
//   Las que van en rejilla usan `auto-fit` con un mínimo, no un número fijo de
//   columnas: cuántos préstamos y cuántos gastos fijos hay es un DATO —uno o
//   cuatro, tres o doce— y un `repeat(3, …)` fijo deja una tarjeta sola con dos
//   huecos al lado el mes que sólo hay una.
//
//   `.cf-kpis` lleva CUATRO columnas fijas y no `auto-fit`: son exactamente
//   cuatro tarjetas —lo cobrado, lo pagado, lo repartible y dónde está la
//   empresa— y no una lista cuya longitud sea un dato. Cuatro cifras en una
//   sola fila es lo que hace que se lean como una sola respuesta; en cuanto una
//   de ellas cae a la línea de abajo vuelve a parecer un bloque aparte, que es
//   justo lo que le pasaba a la posición neta cuando era una tarjeta suelta.
//
//   Y tres bloques se quedan a ancho completo a propósito, porque comprimirlos
//   los rompe:
//
//     · La tabla de gasto por categoría. Dos columnas de cifras más la `note`
//       impresa bajo la fila; a media pantalla la nota se parte y deja de
//       leerse, que es exactamente lo que un tooltip ya hacía mal.
//     · La cascada de reparto. Es una secuencia —excedente, impuestos,
//       post-impuesto, los cuatro tramos, los socios— y las sangrías son su
//       jerarquía. En dos columnas deja de leerse como progresión y pasa a
//       parecer una lista de cosas sueltas.
//     · `Not counted yet`. Es la sección que dice qué NO está confirmado, y
//       encogerla o plegarla es la forma más rápida de que nadie la lea. Con
//       todo lo de alrededor más denso, además, gana prominencia sin tocarla.
// ─────────────────────────────────────────────────────────────────────────────
const COMPANY_FINANCES_RESPONSIVE_CSS = `
/* ─────────────────────────────────────────────────────────────────────────
   El parrafo del backend, plegado

   Una linea por bloque, y el texto largo del endpoint detras de ella. El
   marcador nativo se quita y lo sustituye un chevron propio que gira al
   abrir, porque el triangulo del navegador no se puede teñir con los tokens
   del tema y en oscuro queda como una mancha.

   Sin acentos ni comillas invertidas en estos comentarios: esto vive dentro
   de un template literal de JS y una comilla invertida lo cerraria.
   ───────────────────────────────────────────────────────────────────────── */
.cf-note > summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--text-muted);
  user-select: none;
}
.cf-note > summary::-webkit-details-marker { display: none; }
.cf-note > summary:hover { color: var(--text-secondary); }
.cf-note > summary:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 3px; }
.cf-note .cf-note-chevron { flex-shrink: 0; margin-top: 2px; transition: transform 120ms ease; }
.cf-note[open] .cf-note-chevron { transform: rotate(90deg); }
.cf-note .cf-note-body {
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: 12px;
  line-height: 1.55;
  color: var(--text-secondary);
}
@media (max-width: 1400px) {
  /* Cuatro tarjetas de cabecera dejan de caber mucho antes que las rejillas
     de tres, asi que bajan a dos en su propio corte y no en el de 1100. */
  .cf-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
}
@media (max-width: 1100px) {
  .cf-debt { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .cf-loans { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .cf-recurring { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
}
@media (max-width: 768px) {
  .cf-filters { flex-direction: column !important; align-items: stretch !important; }
  .cf-filters > * { width: 100% !important; }
  .cf-range { flex-direction: column !important; align-items: stretch !important; }
  .cf-range > * { width: 100% !important; }
  .cf-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
  .cf-header .btn { width: 100% !important; }
  .cf-kpis { grid-template-columns: 1fr !important; }
  .cf-debt { grid-template-columns: 1fr !important; }
  .cf-loans { grid-template-columns: 1fr !important; }
  .cf-recurring { grid-template-columns: 1fr !important; }
  .cf-tax { grid-template-columns: 1fr !important; }
  .cf-modal { width: 100% !important; max-width: 100% !important; }
  .cf-input { font-size: 16px !important; }
  .cf-form-row { grid-template-columns: 1fr !important; }
  .cf-monthpicker > div { flex: 1 !important; min-width: 0 !important; }
  .cf-monthpicker select { width: 100% !important; min-width: 0 !important; }
}
`

// Los `kind` de Vendor, tal cual los declara corporate/models.py. Sólo se usan
// en el alta en línea; el backend acepta el default 'other' si no se manda.
const VENDOR_KINDS = [
  { value: 'software', label: 'Software / SaaS' },
  { value: 'bank', label: 'Bank' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'service', label: 'Service provider' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
]

// ─────────────────────────────────────────────────────────────────────────────
//   Fechas
//
//   Todo con aritmética de cadena, nunca `new Date().toISOString()`. El mes que
//   se está mirando es un dato local del usuario, y toISOString() lo pasa a UTC:
//   el 1 de marzo a las 00:30 en Madrid devolvería '2026-02-28'. Es el mismo
//   razonamiento que documenta `fmtDay` en components/financial/formatting.js.
// ─────────────────────────────────────────────────────────────────────────────
const currentMonth = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

// El backend filtra por `entry_date` (no por `created_at`), así que una factura
// de enero capturada en marzo cae en enero — que es lo que quiere quien mira
// "el gasto de enero".
//
// Un rango son el primer día del mes "desde" y el último del mes "hasta". Es
// una VENTANA de consulta, no una cifra: aquí no se agrega nada, sólo se
// traducen dos meses a las dos fechas que la tabla de movimientos necesita como
// parámetros. Los totales del rango los calcula `/corporate/overview`.
//
// `new Date(y, m, 0)` es el día 0 del mes siguiente, o sea el último del mes
// pedido, y sale en hora local: se usa sólo su `getDate()`, un número del 28 al
// 31, así que el huso no entra en el resultado.
const lastDayOf = (ym) => {
  const [y, m] = String(ym).split('-').map(Number)
  if (!y || !m) return null
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`
}

const rangeBounds = (from, to) => {
  const end = lastDayOf(to)
  if (!/^\d{4}-\d{2}$/.test(String(from)) || !end) {
    return { date_from: undefined, date_to: undefined }
  }
  return { date_from: `${from}-01`, date_to: end }
}

const fmtDay = (ymd) => {
  if (!ymd) return '—'
  const [y, m, d] = String(ymd).split('-').map(Number)
  if (!y || !m || !d) return String(ymd)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ─────────────────────────────────────────────────────────────────────────────
//   El 403 no es un error, es una respuesta
//
//   Los endpoints de corporate exigen pertenecer al grupo `Finance`, que no es
//   is_staff ni is_superuser: `corporate/permissions.py` lo dice explícitamente
//   y un superusuario tampoco está exento. El objeto `user` del frontend no
//   lleva ni grupos ni permisos (sólo is_superuser / is_staff / company_id /
//   must_change_password), así que este acceso NO se puede resolver en cliente:
//   se pregunta al servidor y se acata la respuesta.
//
//   Y ahora mismo el 403 es el camino normal, no el raro — la migración
//   0002_finance_group crea el grupo VACÍO a propósito, para que `migrate` no
//   conceda acceso a nadie. Hasta que alguien añada al primer miembro, esto es
//   lo que ve todo el mundo.
// ─────────────────────────────────────────────────────────────────────────────
function AccessDeniedNotice({ detail }) {
  return (
    <div className="card fade-in" style={{ maxWidth: 620, margin: '0 auto', textAlign: 'center', padding: '36px 28px' }}>
      <div
        style={{
          width: 52, height: 52, borderRadius: '50%', margin: '0 auto 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--amber-bg)', color: 'var(--amber)',
        }}
      >
        <Lock size={24} />
      </div>
      <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Finance group required</h2>
      {/* El mensaje del backend, literal. `InFinanceGroup.message` ya explica
          que la pertenencia se concede explícitamente y que ni staff ni
          superuser bastan; reescribirlo aquí sería una segunda versión de la
          política que se desincroniza en cuanto cambie la de allí. */}
      <p style={{ color: 'var(--text-secondary)', margin: '0 0 8px', lineHeight: 1.55 }}>{detail}</p>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0, lineHeight: 1.55 }}>
        Ask an administrator to add your account to the <strong>Finance</strong> group. It is granted
        person by person and on the record — being staff, or even a superuser, does not carry it.
      </p>
    </div>
  )
}

// El 403 acotado: el libro se lee, el resumen del mes no. Ver `loadOverview`.
// No reutiliza `AccessDeniedNotice` porque no dice lo mismo — aquella corta la
// pantalla entera, y esta tiene que dejar claro que lo que falta es un permiso
// más, no el acceso al módulo.
function OverviewDeniedNotice({ detail }) {
  return (
    <div
      className="card"
      style={{ marginBottom: 16, borderLeft: '3px solid var(--amber)', background: 'var(--bg-raised)' }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 1 }}>
          <Lock size={17} />
        </div>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Monthly overview not available to you</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            {detail}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            It carries what each partner is distributed, so it asks for a second permission on top of
            reading the books. The expenses below are unaffected.
          </p>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   El 422 del rango: un backend que todavía no lo sirve
//
//   `?from=&to=` es nuevo en `/corporate/overview`. Contra un despliegue
//   anterior, `month` sigue siendo obligatorio y la petición se rechaza con un
//   422 de validación —cuyo `detail` es la lista de ninja, no una cadena, así
//   que `errorDetail` cae al texto de aquí en vez de pintar un objeto.
//
//   Se dice explícitamente en vez de con un toast de "no se pudo cargar": el
//   overview vacío tiene una causa concreta y accionable, y confundirla con una
//   caída lleva a buscar el fallo en el sitio equivocado. El libro de abajo no
//   depende de esta llamada y se sigue leyendo.
// ─────────────────────────────────────────────────────────────────────────────
function RangeUnsupportedNotice({ detail }) {
  return (
    <div
      className="card"
      style={{ marginBottom: 16, borderLeft: '3px solid var(--amber)', background: 'var(--amber-bg)' }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 1 }}>
          <TriangleAlert size={17} />
        </div>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>The overview refused this window</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            {detail}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            Ranges are new on this endpoint. If the backend deploy that added them has not landed
            yet, every window is refused — including a single month, which is sent as a range of
            one. The expenses below are unaffected.
          </p>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   `MonthTotals` se ha eliminado, y con él la llamada a
//   `/corporate/cash-entries/summary`
//
//   Pintaba el gasto del mes por categoría desde `summary.by_category`, que es
//   lo mismo que ahora trae `overview.expense.by_category` y mejor: aquél daba
//   un solo `out_total` por línea, y éste separa `cash_amount` de
//   `expense_amount` con la nota que explica por qué difieren.
//
//   Mantener los dos bloques habría puesto el gasto del mismo mes dos veces en
//   la misma pantalla, calculado con dos criterios distintos —`summary` suma
//   todo OUT, `overview` es base caja con sus propias reglas— y dos totales que
//   pueden discrepar sin que nadie sepa cuál es el bueno es exactamente lo que
//   este módulo existe para evitar.
//
//   El badge `derived` que sólo traía `summary` NO se ha perdido: es la señal
//   de doble contabilización y por eso el campo se ha añadido a
//   `ExpenseCategoryOut` en el backend. `ExpenseByCategory` lo lee con
//   `=== true`, así que una respuesta anterior a ese despliegue simplemente no
//   lo pinta en vez de romperse.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//   Alta de proveedor, en línea desde el modal de gasto
//
//   No hay pantalla de vendors y no la va a haber pronto. Sin esto, el primer
//   proveedor nuevo deja el registro de un gasto bloqueado sin salida.
//
//   Categorías NO llevan equivalente a propósito: el plan de cuentas es
//   estructura contable, no un dato operativo. Improvisarlo desde un modal
//   acaba en quince categorías solapadas y ningún informe que cuadre.
// ─────────────────────────────────────────────────────────────────────────────
// `onResolved(vendor, { created })` y no `onCreated`: este componente termina
// por dos caminos —dio de alta un proveedor, o encontró que ya existía— y sólo
// el primero añade una fila a la lista. Un `onCreated` que también dispara
// cuando no se creó nada es exactamente cómo se coló la fila duplicada.
function VendorQuickAdd({ vendors, onCancel, onResolved }) {
  const [name, setName] = useState('')
  const [kind, setKind] = useState('other')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    const clean = name.trim()
    if (!clean) { toast.error('Enter a vendor name'); return }
    // `Vendor.name` es unique en el backend, pero `service.create_vendor` va
    // directo al `objects.create()` sin comprobarlo: un duplicado revienta como
    // IntegrityError y sale 500, no un 4xx que se pueda leer. Lo comprobamos
    // aquí, sin distinguir mayúsculas, contra la lista ya cargada. No es una
    // validación de verdad —dos pestañas a la vez siguen pudiendo chocar— pero
    // convierte el caso corriente (teclear "AWS" cuando "aws" ya existe) en un
    // mensaje útil en vez de un error de servidor.
    const clash = vendors.find((v) => v.name.trim().toLowerCase() === clean.toLowerCase())
    if (clash) {
      toast.error(`"${clash.name}" already exists — pick it from the list.`)
      // Ya está en la lista: sólo se selecciona. Volver a insertarlo dejaría
      // dos filas con el mismo id en el desplegable.
      onResolved(clash, { created: false })
      return
    }
    setSaving(true)
    try {
      const res = await api.post('/corporate/vendors', { name: clean, kind })
      toast.success('Vendor added')
      onResolved(res.data, { created: true })
    } catch (err) {
      toast.error(errorDetail(err, 'Could not add the vendor'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        padding: 12, background: 'var(--bg-raised)', display: 'flex',
        flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>New vendor</div>
      <div className="cf-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <input
          className="input cf-input" type="text" autoFocus placeholder="Vendor name"
          value={name} onChange={(e) => setName(e.target.value)}
        />
        <select className="select cf-input" value={kind} onChange={(e) => setKind(e.target.value)}>
          {VENDOR_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? 'Adding…' : 'Add vendor'}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Modal: registrar un GASTO
// ─────────────────────────────────────────────────────────────────────────────
function ExpenseFormModal({ categories, vendors, onVendorAdded, onClose, onSaved }) {
  const [date, setDate] = useState(todayISO())
  const [categoryId, setCategoryId] = useState('')
  const [vendorId, setVendorId] = useState('')
  const [amount, setAmount] = useState('')
  const [memo, setMemo] = useState('')
  const [addingVendor, setAddingVendor] = useState(false)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    const amt = Number(amount)
    if (!date) { toast.error('Select a date'); return }
    if (!categoryId) { toast.error('Pick a category'); return }
    if (!vendorId) { toast.error('Pick a vendor'); return }
    if (!amt || amt <= 0) { toast.error('Enter a valid amount greater than 0'); return }
    setSaving(true)
    try {
      const res = await api.post('/corporate/cash-entries', {
        // Fijo. Ver la cabecera del archivo: esta pantalla registra gasto.
        direction: 'out',
        entry_date: date,
        amount: amt,
        category_id: Number(categoryId),
        vendor_id: Number(vendorId),
        memo: memo || null,
      })
      toast.success('Expense recorded')
      onSaved(res.data?.entry || null)
    } catch (err) {
      // El 422 de corporate llega como {"detail": "..."} y trae el motivo real:
      // DerivedCategoryNotWritable (una línea que calcula financial/),
      // CatalogRowInactive (categoría o proveedor desactivado entre la carga y
      // el envío), InvalidAmount. Se muestra tal cual — el backend explica el
      // rechazo mejor de lo que puede esta pantalla, que no conoce sus reglas.
      toast.error(errorDetail(err, 'Could not record the expense'))
    } finally {
      setSaving(false)
    }
  }

  // Las categorías derivadas no admiten asientos manuales: `corporate.service`
  // rechaza cualquiera dirigido a una, y la razón es que su cifra ya la calcula
  // `financial/` desde los leads. Filtrarlas del desplegable es lo que evita
  // ofrecer un camino que el backend siempre va a rechazar.
  const selectable = useMemo(() => categories.filter((c) => !c.is_derived), [categories])
  const noCategories = selectable.length === 0

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card cf-modal" style={{ maxWidth: 500, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>Record expense</h3>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>

        {noCategories ? (
          // El plan de cuentas viene vacío en producción: el backend lo dice
          // explícitamente ("No data seed either — the chart of accounts gets
          // its own PR"). Sin categorías no se puede registrar nada, y un
          // desplegable vacío parecería un fallo de carga en vez de lo que es.
          <div style={{ padding: '8px 0 4px' }}>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 8px', lineHeight: 1.55 }}>
              The chart of accounts is empty, so there is nothing to file an expense under yet.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0, lineHeight: 1.55 }}>
              Account categories are accounting structure, not something to improvise per expense —
              they are loaded deliberately, not from this screen. Ask whoever keeps the books to set
              them up first.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="cf-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label" htmlFor="cf-date">Date</label>
                  <input
                    id="cf-date" className="input cf-input" type="date"
                    value={date} onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label" htmlFor="cf-amount">Amount (USD)</label>
                  <input
                    id="cf-amount" className="input cf-input" type="number" min="0" step="0.01" placeholder="0.00"
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="form-label" htmlFor="cf-category">Category</label>
                <select
                  id="cf-category" className="select cf-input"
                  value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">Select a category…</option>
                  {selectable.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <label className="form-label" htmlFor="cf-vendor">Vendor</label>
                  {!addingVendor && (
                    <button
                      className="btn btn-ghost btn-sm" type="button"
                      style={{ padding: '2px 8px', fontSize: 12 }}
                      onClick={() => setAddingVendor(true)}
                    >
                      <Plus size={13} style={{ marginRight: 4 }} /> New vendor
                    </button>
                  )}
                </div>
                {addingVendor ? (
                  <VendorQuickAdd
                    vendors={vendors}
                    onCancel={() => setAddingVendor(false)}
                    onResolved={(vendor, { created }) => {
                      setAddingVendor(false)
                      if (!vendor?.id) return
                      // Los dos caminos seleccionan; sólo el alta amplía la
                      // lista. Uno que ya existía viene de esa misma lista.
                      if (created) onVendorAdded(vendor)
                      setVendorId(String(vendor.id))
                    }}
                  />
                ) : (
                  <select
                    id="cf-vendor" className="select cf-input"
                    value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                  >
                    <option value="">
                      {vendors.length === 0 ? 'No vendors yet — add one' : 'Select a vendor…'}
                    </option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                )}
              </div>

              <div>
                <label className="form-label" htmlFor="cf-memo">
                  Memo <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
                </label>
                <input
                  id="cf-memo" className="input cf-input" type="text" placeholder="e.g. invoice #, what it covers"
                  value={memo} onChange={(e) => setMemo(e.target.value)}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || addingVendor}>
                {saving ? 'Saving…' : 'Record'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Tabla de movimientos
//
//   No reutiliza `MovementHistoryTable` de components/financial/: aquella tiene
//   las columnas de un pago/cobro (referencia, nota) y ofrece editar, que aquí
//   no existe — una corrección es una anulación más un asiento nuevo, igual que
//   en `financial/`. El tratamiento visual de la fila anulada sí es el mismo a
//   propósito: atenuada, importe tachado, motivo y autor a la vista.
// ─────────────────────────────────────────────────────────────────────────────
function ExpensesTable({ rows, onVoid }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Category</th>
            <th>Vendor</th>
            <th>Amount</th>
            <th>Memo</th>
            <th style={{ width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={r.is_voided ? { opacity: 0.55 } : undefined}>
              <td style={{ whiteSpace: 'nowrap' }}>
                {fmtDay(r.entry_date)}
                {r.is_voided && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
                    <span
                      className="badge badge-gray"
                      title={r.voided_at ? `Annulled ${fmtDateTime(r.voided_at)}` : 'Annulled'}
                    >
                      Annulled
                    </span>
                    {r.void_reason && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.void_reason}</span>
                    )}
                    {r.voided_by_email && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>by {r.voided_by_email}</span>
                    )}
                  </div>
                )}
              </td>
              <td>{r.category_name}</td>
              <td>{r.vendor_name}</td>
              <td style={{ fontWeight: 600, textDecoration: r.is_voided ? 'line-through' : 'none' }}>
                {money(r.amount)}
              </td>
              <td style={{ color: 'var(--text-muted)' }}>{r.memo || '—'}</td>
              <td>
                {/* Una fila anulada no lleva acción: el backend contesta 409
                    (AlreadyVoided) a un segundo intento. Ofrecer el botón sería
                    una trampa. */}
                {!r.is_voided && (
                  <button className="btn btn-danger btn-icon btn-sm" title="Annul" onClick={() => onVoid(r)}>🚫</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Página
// ─────────────────────────────────────────────────────────────────────────────
export default function CompanyFinancesPage() {
  // ───────────────────────────────────────────────────────────────────────────
  //   La ventana: dos meses, y casi siempre el mismo dos veces
  //
  //   Un solo estado con los dos extremos, arrancando los dos en el mes actual.
  //   `month=X` y `from=X&to=X` son la misma petición por el mismo código en el
  //   backend, así que la pantalla manda SIEMPRE el rango y no tiene dos
  //   caminos que puedan separarse. Que el caso común siga costando un solo
  //   desplegable es cosa de `MonthRangePicker`, no de aquí.
  // ───────────────────────────────────────────────────────────────────────────
  const [range, setRange] = useState(() => {
    const now = currentMonth()
    return { from: now, to: now }
  })
  const [categoryId, setCategoryId] = useState('')
  const [includeVoided, setIncludeVoided] = useState(false)

  const [overview, setOverview] = useState(null)
  const [recurring, setRecurring] = useState([])
  const [registering, setRegistering] = useState(null)
  const [entries, setEntries] = useState([])
  const [categories, setCategories] = useState([])
  const [vendors, setVendors] = useState([])

  const [loading, setLoading] = useState(true)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [denied, setDenied] = useState(null)
  // Distinto de `denied` a propósito. Ver `loadOverview`.
  const [overviewDenied, setOverviewDenied] = useState(null)
  // Distinto de los dos anteriores: no es un permiso, es una ventana que este
  // backend no sirve. Ver `RangeUnsupportedNotice`.
  const [rangeUnsupported, setRangeUnsupported] = useState(null)
  const [recurringLoading, setRecurringLoading] = useState(true)
  const [recurringDenied, setRecurringDenied] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [voiding, setVoiding] = useState(null)

  // ───────────────────────────────────────────────────────────────────────────
  //   El mes de foco, DERIVADO y no sincronizado
  //
  //   Dos bloques —la cascada de reparto y los gastos fijos— son por mes y no
  //   por ventana, y toman su mes de aquí.
  //
  //   `focusMonth` guarda lo que el usuario eligió, y el mes que se usa se
  //   deriva de él contra la lista que devuelve el backend. Así, cuando alguien
  //   cambia el rango y el mes elegido se queda fuera, el foco vuelve solo al
  //   último mes de la ventana nueva sin un `useEffect` que lo persiga: un
  //   efecto que corrige estado detrás de otro estado tiene siempre un
  //   fotograma en el que la pantalla muestra un mes que no está en el rango.
  //
  //   Las opciones son `overview.months`, del backend. No se enumera el rango
  //   aquí: quién decide qué meses hay entre dos es `period_range`, y una
  //   segunda enumeración en cliente es un sitio donde puede decidirse distinto.
  // ───────────────────────────────────────────────────────────────────────────
  //   No hay `focusLoading`: "todavía no está" es no tener ni cascada ni
  //   motivo, y una bandera aparte para eso es justo lo que se podía quedar por
  //   detrás del estado real. Con dos piezas en vez de tres, los estados del
  //   bloque son exhaustivos por construcción.
  const [focusMonth, setFocusMonth] = useState(null)
  const [focusOverview, setFocusOverview] = useState(null)
  const [focusError, setFocusError] = useState(null)

  const single = range.from === range.to
  const rangeText = rangeLabel(range.from, range.to)
  const rangeMonths = overview?.months || null
  const focus = rangeMonths?.includes(focusMonth) ? focusMonth : range.to

  // ───────────────────────────────────────────────────────────────────────────
  //   Cuándo se pide la cascada de un mes, y por qué NO desde la bandera
  //
  //   La bandera `applies_to_range` es lo que decide qué se PINTA, y eso no
  //   cambia: el componente ramifica por ella y nunca por si falta una clave.
  //   Pero como condición de CARGA llegaba tarde, y el retraso era visible.
  //
  //   Esperar a que la respuesta de la ventana dijera `applies_to_range: false`
  //   metía un render entero entre "ya sé que hay que enseñar la cascada de un
  //   mes" y "ya la he pedido". En ese render la tarjeta anunciaba "las cifras
  //   de abajo son las de septiembre" y debajo no había ni cifras, ni carga, ni
  //   motivo: un hueco silencioso, que es peor que un cero porque un cero se
  //   puede cuestionar.
  //
  //   Que la ventana tiene más de un mes lo sabe esta pantalla sin preguntar
  //   —son los dos extremos que el usuario acaba de elegir— así que la llamada
  //   arranca con eso. No es reimplementar una regla del backend: `single` ya
  //   gobierna medio render, y la FORMA de la respuesta se sigue leyendo de la
  //   bandera.
  //
  //   Los dos rechazos siguen cortando la segunda llamada, que es lo que
  //   aportaba la condición anterior: sin permiso o con la ventana rechazada,
  //   pedir otra vez sólo repetiría el mismo 403 o el mismo 422.
  // ───────────────────────────────────────────────────────────────────────────
  const needsFocusCascade = !single && !overviewDenied && !rangeUnsupported

  // El catálogo se carga una vez; no depende del mes ni del filtro.
  const loadCatalog = useCallback(async () => {
    try {
      const [cRes, vRes] = await Promise.all([
        api.get('/corporate/categories'),
        api.get('/corporate/vendors'),
      ])
      setCategories(cRes.data?.categories || [])
      setVendors(vRes.data?.vendors || [])
    } catch (err) {
      // El 403 lo gobierna `load()`, que es quien pinta la pantalla de acceso
      // denegado. Aquí sólo importa no tapar esa pantalla con un toast: si el
      // usuario no está en Finance, estas dos llamadas fallan igual y decirlo
      // tres veces no añade nada.
      if (!isForbidden(err)) toast.error('Could not load categories and vendors')
    }
  }, [])

  // ───────────────────────────────────────────────────────────────────────────
  //   `/corporate/overview` va en su propia carga, y no dentro de `load()`
  //
  //   Dos razones. Sólo depende del mes: el filtro de categoría y "ver
  //   anuladas" acotan la TABLA de movimientos, no el mes contable, y meterlo
  //   en `load()` haría una llamada grande cada vez que alguien toca un
  //   desplegable que no la cambia.
  //
  //   Y su 403 no significa lo mismo. `/overview` exige `view_cashentry` Y
  //   `view_allocationpolicy` porque la respuesta lleva lo que se distribuye a
  //   cada socio, y el backend mantiene "puede leer los libros" y "puede leer
  //   las reglas de reparto" como dos permisos separados a propósito. Alguien
  //   con el primero y no el segundo puede ver el libro entero, así que tapar
  //   la pantalla con "Finance group required" le negaría algo que sí tiene
  //   derecho a ver. Ese 403 se queda acotado a este bloque.
  // ───────────────────────────────────────────────────────────────────────────
  const loadOverview = useCallback(async () => {
    setOverviewLoading(true)
    try {
      // `from`/`to` siempre, incluso para un mes suelto. `month=X` es
      // `from=X&to=X` por el mismo código en el backend, y mandar los dos a la
      // vez es un 422 a propósito: una petición que nombra un mes y un rango se
      // contradice, y resolverla por precedencia contestaría la mitad que quien
      // llama no quería decir.
      const res = await api.get('/corporate/overview', {
        params: { from: range.from, to: range.to },
      })
      setOverview(res.data || null)
      setOverviewDenied(null)
      setRangeUnsupported(null)
    } catch (err) {
      setOverview(null)
      if (isForbidden(err)) {
        setOverviewDenied(errorDetail(err, 'The monthly overview is restricted.'))
      } else if (err?.response?.status === 422) {
        // La ventana fue rechazada, no falló. El motivo del backend, literal
        // cuando lo trae en texto; el 422 de validación de un despliegue sin
        // rangos lo trae como lista, y ahí sirve el fallback.
        setRangeUnsupported(errorDetail(
          err,
          'The overview did not accept `from` and `to` for this window.',
        ))
      } else {
        toast.error('Could not load the monthly overview')
      }
    } finally {
      setOverviewLoading(false)
    }
  }, [range])

  // ───────────────────────────────────────────────────────────────────────────
  //   La cascada del mes de foco, en su propia llamada
  //
  //   Sobre una ventana de más de un mes el overview NO trae las cifras del
  //   reparto: vienen ausentes, y eso es la garantía que compra el backend —un
  //   null se leería como "no había política" y un 0 como "mes en déficit", que
  //   son otros dos estados que ese bloque distingue. Así que la única forma de
  //   tener la cascada de un mes es pedir ese mes, y por eso hay una segunda
  //   llamada en vez de un `??` sobre la primera.
  //
  //   Va a su propio estado y no toca `overview`: lo demás de la pantalla —los
  //   KPI, el gasto, la deuda, lo no contado— sigue siendo el de la ventana
  //   entera, y volcar aquí la respuesta de un mes convertiría media pantalla
  //   en las cifras de ese mes sin que nada lo dijera.
  // ───────────────────────────────────────────────────────────────────────────
  const loadFocusOverview = useCallback(async () => {
    // Lo primero, y también cuando no hace falta pedir nada: la cascada del mes
    // ANTERIOR no puede sobrevivir un cambio de mes de foco. Unas cifras bajo un
    // encabezado que nombra otro mes son peores que no tener cifras.
    setFocusOverview(null)
    setFocusError(null)
    if (!needsFocusCascade) return

    try {
      const res = await api.get('/corporate/overview', { params: { month: focus } })
      setFocusOverview(res.data || null)
    } catch (err) {
      // El motivo del backend cuando lo trae —un 403 de `view_allocationpolicy`
      // explica el suyo mejor que esta pantalla— y si no, el de aquí. Lo que no
      // puede pasar es que no haya ninguno: el bloque acaba de prometer las
      // cifras de este mes.
      setFocusError(errorDetail(
        err,
        `Could not load the allocation for ${monthLabel(focus)}.`,
      ))
    }
  }, [needsFocusCascade, focus])

  // ───────────────────────────────────────────────────────────────────────────
  //   Los gastos fijos vigentes en el mes
  //
  //   `?in_force_on=` con el ÚLTIMO día del mes, que es el mismo corte que usa
  //   `_recurring_unregistered` en `overview.py`. Una sola definición de "qué
  //   estaba vigente en este mes" para las dos mitades de la pantalla: la lista
  //   con sus botones, y las entradas `recurring_unregistered` de `not_counted`.
  //   Dos cortes distintos darían dos respuestas a la misma pregunta en la
  //   misma pantalla.
  //
  //   Sin `include_closed`: el filtro SUSTITUYE al split abierto/cerrado en vez
  //   de intersecarse con él, así que mandarlo no cambia nada y sugeriría que
  //   sí. Interseccionarlos, que es lo que parecía natural, habría descartado
  //   justo la fila superseded que se está pidiendo.
  // ───────────────────────────────────────────────────────────────────────────
  const loadRecurring = useCallback(async () => {
    setRecurringLoading(true)
    // El ÚLTIMO día del MES DE FOCO, no del rango: este panel responde una
    // pregunta por mes y se ancla a uno. Ver la cabecera de
    // `RecurringExpensesPanel` para por qué no se agrupa por los meses de la
    // ventana.
    const dateTo = lastDayOf(focus)
    try {
      const res = await api.get('/corporate/recurring-expenses', {
        params: { in_force_on: dateTo },
      })
      setRecurring(res.data?.recurring_expenses || [])
      setRecurringDenied(null)
    } catch (err) {
      setRecurring([])
      if (isForbidden(err)) {
        setRecurringDenied(errorDetail(err, 'Standing charges are restricted.'))
      } else {
        toast.error('Could not load the standing charges')
      }
    } finally {
      setRecurringLoading(false)
    }
  }, [focus])

  const load = useCallback(async () => {
    setLoading(true)
    // La tabla de movimientos sigue el rango entero, no el mes de foco: es el
    // libro de la ventana que se está mirando.
    const bounds = rangeBounds(range.from, range.to)
    try {
      const params = {
        ...bounds,
        direction: 'out',
        include_voided: includeVoided,
        ...(categoryId ? { category_id: Number(categoryId) } : {}),
      }
      const eRes = await api.get('/corporate/cash-entries', { params })
      setEntries(eRes.data?.entries || [])
      setDenied(null)
    } catch (err) {
      if (isForbidden(err)) {
        // Respuesta legítima, no incidencia: sin toast y sin dejar la pantalla
        // a medias. Se guarda el `detail` del backend para mostrarlo tal cual.
        setDenied(errorDetail(err, 'The corporate books are restricted to the Finance group.'))
        setEntries([])
      } else {
        toast.error('Could not load company expenses')
      }
    } finally {
      setLoading(false)
    }
  }, [range, categoryId, includeVoided])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadOverview() }, [loadOverview])
  useEffect(() => { loadFocusOverview() }, [loadFocusOverview])
  useEffect(() => { loadRecurring() }, [loadRecurring])
  useEffect(() => { loadCatalog() }, [loadCatalog])

  const voidEntry = async (reason) => {
    try {
      await api.delete(`/corporate/cash-entries/${voiding.id}`, { data: { reason } })
      toast.success('Expense annulled')
      setVoiding(null)
      await load()
    } catch (err) {
      toast.error(errorDetail(err, 'Could not annul the expense'))
      // 409 = otra pestaña la anuló primero. Recargamos para que la fila
      // muestre su estado real en vez de volver a ofrecer el botón.
      if (err?.response?.status === 409) {
        setVoiding(null)
        await load()
      }
    }
  }

  // Sólo las no derivadas, igual que en el formulario: una categoría derivada
  // no admite asientos manuales, así que filtrar por ella sólo puede dar una
  // tabla vacía.
  const filterCategories = useMemo(() => categories.filter((c) => !c.is_derived), [categories])

  if (denied) {
    return (
      <div className="fade-in">
        <style>{COMPANY_FINANCES_RESPONSIVE_CSS}</style>
        <div className="page-header">
          <div className="hero-kicker">Financial</div>
          <h1>Company finances</h1>
        </div>
        <AccessDeniedNotice detail={denied} />
      </div>
    )
  }

  return (
    <div className="fade-in">
      <style>{COMPANY_FINANCES_RESPONSIVE_CSS}</style>

      <div className="page-header cf-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div className="hero-kicker">Financial</div>
          <h1>Company finances</h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: 720 }}>
            What the company itself spends — software, insurance, bank charges, contractors. Not
            lead economics: what buyers owe us and what affiliates are owed lives in Accounts
            receivable and Accounts payable.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
          <Receipt size={15} style={{ marginRight: 6 }} /> Record expense
        </button>
      </div>

      {/* ───────────────────────────────────────────────────────────────────
          El rango, arriba y gobernando la pantalla ENTERA

          Vivía abajo, junto a los filtros de la tabla, cuando era el mes de un
          resumen que estaba encima. Ahora acota todo lo que se ve —los KPI, el
          gasto, la deuda, lo no contado y también la tabla de movimientos— y un
          control que acota la pantalla entera no puede estar al pie de ella.

          Los otros dos filtros se quedan donde estaban a propósito: categoría y
          "ver anuladas" acotan sólo la TABLA, y subirlos aquí los haría parecer
          filtros del resumen, que no lo son. Lo que gobierna todo va arriba; lo
          que gobierna una tabla va con la tabla.
          ─────────────────────────────────────────────────────────────────── */}
      <div
        className="card cf-range"
        style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}
      >
        <MonthRangePicker from={range.from} to={range.to} onChange={setRange} idPrefix="cf-filter" />
        {/* Sólo aparece con más de un mes: con uno suelto el foco ES ese mes y
            no hay nada que elegir. `rangeMonths` viene del backend, así que el
            control no existe hasta que hay respuesta que lo pueble. */}
        <FocusMonthPicker months={rangeMonths} value={focus} onChange={setFocusMonth} />
      </div>

      {overviewDenied ? (
        <OverviewDeniedNotice detail={overviewDenied} />
      ) : rangeUnsupported ? (
        <RangeUnsupportedNotice detail={rangeUnsupported} />
      ) : (
        <>
          {/* Cuatro tarjetas en una fila, y la posición neta es la cuarta en
              vez de un bloque suelto debajo. `rangeText` rotula SÓLO la mitad
              de arriba de esa cuarta —el déficit de la ventana—; la de abajo se
              rotula con el `as_of` que trae el propio bloque, que es el motivo
              por el que el backend lo manda. */}
          <HeadlineKpis
            headline={overview?.headline}
            position={overview?.position}
            rangeText={rangeText}
            loading={overviewLoading}
          />

          <BankDebtPanel bankDebt={overview?.bank_debt} monthTo={range.to} loading={overviewLoading} />

          {/* Junto a las cifras y siempre visible, nunca detrás de un tooltip
              ni de un desplegable: sin esta lista la pantalla afirma más de lo
              que sabe, y son las finanzas de quien la está mirando.

              Sobre un rango es además el bloque que cubre los meses que NO son
              el de foco: sus entradas traen `month` y se agrupan por él. */}
          <NotCountedPanel
            items={overview?.not_counted}
            rangeText={rangeText}
            single={single}
            loading={overviewLoading}
          />

          {/* Justo debajo de `not_counted` a propósito: sus entradas
              `recurring_unregistered` nombran los meses sin registrar, y el
              botón que los registra queda a continuación en vez de en otra
              parte de la pantalla. */}
          <RecurringExpensesPanel
            rows={recurring}
            month={focus}
            rangeText={rangeText}
            single={single}
            loading={recurringLoading}
            denied={recurringDenied}
            onRegister={setRegistering}
          />

          <ExpenseByCategory expense={overview?.expense} rangeText={rangeText} loading={overviewLoading} />
          <TaxLines tax={overview?.tax} loading={overviewLoading} />
          <AllocationWaterfall
            allocation={overview?.allocation}
            month={focus}
            rangeText={rangeText}
            loading={overviewLoading}
            focusAllocation={focusOverview?.allocation}
            focusError={focusError}
          />
        </>
      )}

      <div
        className="card cf-filters"
        style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}
      >
        <div style={{ minWidth: 200 }}>
          <label className="form-label" htmlFor="cf-filter-category">Category</label>
          <select
            id="cf-filter-category" className="select cf-input"
            value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">All categories</option>
            {filterCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', paddingBottom: 8 }}>
          <input type="checkbox" checked={includeVoided} onChange={(e) => setIncludeVoided(e.target.checked)} />
          Show annulled
        </label>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : entries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-muted)' }}>
          No expenses for {rangeText}{categoryId ? ' in this category' : ''}.
        </div>
      ) : (
        <ExpensesTable rows={entries} onVoid={setVoiding} />
      )}

      {showForm && (
        <ExpenseFormModal
          categories={categories}
          vendors={vendors}
          onVendorAdded={(vendor) => setVendors((prev) => [...prev, vendor].sort((a, b) => a.name.localeCompare(b.name)))}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load() }}
        />
      )}

      {registering && (
        <RegisterRecurringModal
          recurring={registering}
          month={focus}
          onClose={() => setRegistering(null)}
          onRegistered={() => {
            setRegistering(null)
            // Las tres cargas: el asiento nuevo cambia el gasto del mes y la
            // cascada (`overview`), la tabla de movimientos (`load`) y el
            // estado de reclamación de la propia fila (`loadRecurring`).
            // Refrescar sólo una dejaría la pantalla contándose dos versiones.
            loadOverview()
            loadFocusOverview()
            load()
            loadRecurring()
          }}
        />
      )}

      {voiding && (
        <VoidConfirmModal
          noun="expense"
          onCancel={() => setVoiding(null)}
          onConfirm={voidEntry}
        />
      )}
    </div>
  )
}
